import { useEffect } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import {
  CheckLg,
  ExclamationTriangleFill,
  ClockFill,
  ShieldCheck,
  Bank,
  People,
  Headset,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../routes/routes";
import { useAppSelector } from "../../../store/redux-hooks";
import { MILISECONDS_IN_DAY } from "../../../util/constants/constants";

// Feature item component for reuse
const FeatureItem = ({
  icon,
  title,
  description,
  isPrimary = true,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  isPrimary?: boolean;
}) => (
  <div className="feature-item">
    <div className={`feature-icon ${isPrimary ? "primary" : "secondary"}`}>
      {icon}
    </div>
    <div className="feature-content">
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  </div>
);

// FAQ item component
const FAQItem = ({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) => (
  <div className="faq-item">
    <h3>{question}</h3>
    <p>{answer}</p>
  </div>
);

export default () => {
  const navigate = useNavigate();
  const loadedCompany = useAppSelector((state) => state.company.loadedCompany);

  // Calculate trial remaining days
  const getTrialStatus = () => {
    if (!loadedCompany?.trialExpirationDate) {
      return { isExpired: true, daysRemaining: 0 };
    }

    const trialRemainingMS =
      new Date(loadedCompany.trialExpirationDate).getTime() -
      new Date().getTime();

    if (trialRemainingMS <= 0) {
      return { isExpired: true, daysRemaining: 0 };
    }

    const daysRemaining = Math.ceil(trialRemainingMS / MILISECONDS_IN_DAY);
    return { isExpired: false, daysRemaining };
  };

  const { isExpired, daysRemaining } = getTrialStatus();

  // Add trial-ended-active class to body for full-page layout
  useEffect(() => {
    document.body.classList.add("trial-ended-active");
    return () => {
      document.body.classList.remove("trial-ended-active");
    };
  }, []);

  const handleActivate = () => {
    navigate(RoutePaths.BILLING_START_SUBSCRIPTION);
  };

  const price = "€49.99";
  const ctaText = "Activate Subscription";

  // Banner content based on trial status
  const getBannerContent = () => {
    if (isExpired) {
      return {
        icon: <ExclamationTriangleFill size={20} className="alert-icon" />,
        title: "Your free trial has ended. Don't worry—your data is safe.",
        description:
          "To unlock your dashboard, send invoices, and automate your tax returns, simply activate your subscription below.",
        bannerClass: "trial-alert-banner expired",
      };
    }

    const daysText = daysRemaining === 1 ? "1 day" : `${daysRemaining} days`;
    return {
      icon: <ClockFill size={20} className="alert-icon trial-active" />,
      title: `Your free trial ends in ${daysText}. Activate your subscription now.`,
      description:
        "Activate your subscription today to continue using all features.",
      bannerClass: "trial-alert-banner active",
    };
  };

  const bannerContent = getBannerContent();

  return (
    <div className="trial-ended-page">
      {/* Alert Banner */}
      <div className={bannerContent.bannerClass}>
        <Container>
          <div className="alert-content">
            {bannerContent.icon}
            <div className="alert-text">
              <strong>{bannerContent.title}</strong>
              <p>{bannerContent.description}</p>
            </div>
          </div>
        </Container>
      </div>

      {/* Hero Section */}
      <section className="trial-hero-section">
        <Container>
          <h1>
            The All-In-One
            <br />
            Tax &amp; Accounting Toolkit for Irish Business
          </h1>
          <p className="hero-subtitle">
            Everything you need to satisfy Revenue, get paid, and grow—without
            the stress.
          </p>
        </Container>
      </section>

      {/* Pricing Card Section */}
      <section className="trial-pricing-section">
        <Container>
          <div className="pricing-card">
            <div className="price-display">
              <span className="price">{price}</span>
              <span className="period">/month</span>
            </div>
            <p className="price-note">Fixed price. No hidden fees.</p>

            <ul className="pricing-benefits">
              <li>
                <CheckLg size={14} />
                <span>Cancel anytime. No long-term contracts.</span>
              </li>
              <li>
                <CheckLg size={14} />
                <span>1 Active User (You).</span>
              </li>
              <li>
                <CheckLg size={14} />
                <span>
                  Free Accountant Access (Inviting your accountant never costs
                  extra).
                </span>
              </li>
            </ul>

            <Button className="pricing-cta-btn" onClick={handleActivate}>
              {ctaText}
            </Button>

            <p className="security-note">
              Takes less than 2 minutes. Secure 256-bit SSL Encryption.
            </p>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="trial-features-section">
        <Container>
          <Row>
            {/* The Essentials Column */}
            <Col lg={6}>
              <div className="features-column">
                <div className="features-header">
                  <h2>The Essentials</h2>
                  <p>
                    Your subscription covers all the tools you need to stay
                    compliant and solvent.
                  </p>
                </div>

                <div className="features-list">
                  <FeatureItem
                    icon={<Bank size={20} />}
                    title="Automated Irish VAT & Tax"
                    description="Built specifically for Irish tax rules. We automate the heavy lifting for your VAT and annual returns so you don't have to worry about the Revenue Commissioners."
                    isPrimary
                  />
                  <FeatureItem
                    icon={
                      <svg width="15" height="20" viewBox="0 0 15 20" fill="none">
                        <path
                          d="M0 0V20H15V6L9 0H0ZM9 2L13 6H9V2ZM2 18V2H7V8H13V18H2Z"
                          fill="#212121"
                        />
                      </svg>
                    }
                    title="Unlimited Invoicing"
                    description="Create professional invoices, track open payments, and get paid faster. No limits on how many you send."
                    isPrimary
                  />
                  <FeatureItem
                    icon={<ShieldCheck size={20} />}
                    title="Secure Bank Connections"
                    description="Securely connect your bank to reconcile payments automatically in the background. (AISP Authorised by the Central Bank of Ireland)."
                    isPrimary
                  />
                  <FeatureItem
                    icon={
                      <svg width="22" height="20" viewBox="0 0 22 20" fill="none">
                        <path
                          d="M20 0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H7V18H5V20H17V18H15V16H20C21.1 16 22 15.1 22 14V2C22 0.9 21.1 0 20 0ZM20 14H2V2H20V14Z"
                          fill="#212121"
                        />
                      </svg>
                    }
                    title="Unlimited Receipt Storage"
                    description="Snap and store unlimited receipts. Never lose a tax deduction again. (No storage limits)."
                    isPrimary
                  />
                </div>
              </div>
            </Col>

            {/* Bonus Growth Tools Column */}
            <Col lg={6}>
              <div className="features-column">
                <div className="features-header">
                  <h2>Bonus Growth Tools</h2>
                  <p>
                    Powerful tools included at no extra cost, ready whenever you
                    decide to grow.
                  </p>
                </div>

                <div className="features-list">
                  <FeatureItem
                    icon={<People size={20} />}
                    title="Full Payroll"
                    description="Run payroll for yourself or staff."
                    isPrimary={false}
                  />
                  <FeatureItem
                    icon={
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M10 0C4.5 0 0 4.5 0 10C0 15.5 4.5 20 10 20C15.5 20 20 15.5 20 10C20 4.5 15.5 0 10 0ZM10 18C5.6 18 2 14.4 2 10C2 5.6 5.6 2 10 2C14.4 2 18 5.6 18 10C18 14.4 14.4 18 10 18ZM10.5 5H9V11L14.2 14.2L15 12.9L10.5 10.2V5Z"
                          fill="#212121"
                        />
                      </svg>
                    }
                    title="Projects & Time Tracking"
                    description="Track billable hours and convert them to invoices."
                    isPrimary={false}
                  />
                  <FeatureItem
                    icon={
                      <svg width="15" height="20" viewBox="0 0 15 20" fill="none">
                        <path
                          d="M0 0V20H15V0H0ZM13 18H2V2H13V18ZM3 4H12V6H3V4ZM3 8H12V10H3V8ZM3 12H8V14H3V12Z"
                          fill="#212121"
                        />
                      </svg>
                    }
                    title="Proposals"
                    description="Send professional estimates to win new work."
                    isPrimary={false}
                  />
                  <FeatureItem
                    icon={
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z"
                          fill="#212121"
                        />
                      </svg>
                    }
                    title="Multi-Currency"
                    description="Handle international clients with ease."
                    isPrimary={false}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Trust Section */}
      <section className="trial-trust-section">
        <Container>
          <p className="trust-headline">
            Join 1,000+ Irish businesses who trust Bullet.
          </p>
          <div className="trust-badges">
            <div className="trust-badge">
              <Bank size={24} />
              <span>Central Bank of Ireland Authorised</span>
            </div>
            <div className="trust-badge">
              <svg width="21" height="24" viewBox="0 0 21 24" fill="none">
                <path
                  d="M10.5 0L0 4V12C0 18.6 4.5 24 10.5 24C16.5 24 21 18.6 21 12V4L10.5 0Z"
                  fill="#166534"
                />
                <path d="M7 12L10.5 8L14 12L10.5 16L7 12Z" fill="#FF883E" />
              </svg>
              <span>Made in Ireland</span>
            </div>
            <div className="trust-badge">
              <Headset size={24} />
              <span>Live Expert Support</span>
            </div>
          </div>
        </Container>
      </section>

      {/* Secondary CTA Section */}
      <section className="trial-secondary-cta">
        <Container>
          <div className="secondary-cta-content">
            <Button className="pricing-cta-btn" onClick={handleActivate}>
              {ctaText}
            </Button>
            <p className="security-note">
              Takes less than 2 minutes. Secure 256-bit SSL Encryption.
            </p>
          </div>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="trial-faq-section">
        <Container>
          <h2>Frequently Asked Questions</h2>

          <div className="faq-list">
            <FAQItem
              question="I'm a Sole Trader, is this right for me if I don't need Payroll?"
              answer="Absolutely. While Bullet includes payroll and project tools for free, the core subscription is designed to automate your Invoicing, VAT, and Income Tax. Even without using the extra features, Bullet usually pays for itself by saving you hours on admin and reducing your accountant's bill."
            />
            <FAQItem
              question="Is my financial data secure?"
              answer="Yes. Bullet is the only accounting software authorised by the Central Bank of Ireland. We use bank-grade security, we are fully GDPR compliant, and we never sell your data to third parties."
            />
            <FAQItem
              question="What kind of support will I get?"
              answer="You get access to real human experts, not bots. Our team is based here in Ireland and understands Irish tax and business rules, so we can help you navigate both the software and your compliance questions."
            />
            <FAQItem
              question="Am I tied into a contract?"
              answer="No. You can cancel your subscription at any time with one click. There are no cancellation fees and no long-term lock-ins. If you decide to leave, you can easily export your data—it belongs to you."
            />
            <FAQItem
              question="Is Bullet a reliable company?"
              answer="Yes. We are the only Irish Central bank regulated Irish accounting software. We are a fully self-funded Irish company that has been in business since 2010. We are trusted by thousands of Irish businesses to keep them compliant and solvent."
            />
          </div>
        </Container>
      </section>
    </div>
  );
};
